import { DateTime } from "luxon";

import { OmnipodConstants } from "@/lib/drivers/insulet/OmnipodConstants";
import {
  OmnipodData,
  OmnipodLastUploadInfo,
  OmnipodProfile,
  omnipodVersionToString,
} from "@/lib/drivers/insulet/OmnipodTypes";
import { OmnipodTransformer } from "@/lib/drivers/insulet/transformers/OmnipodTransformer";
import { getBasalInsulinPerDay } from "@/lib/utils/basalInsulinPerDay.ts";
import { InsulinPumpProfileDTO, InsulinPumpSegmentDTO, MedicalDeviceDTO } from "@/models/DeviceModel";
import { DateUtils } from "@/utils/dateUtils.ts";

type Devices = {
  pumpDevice: MedicalDeviceDTO;
  cgmDevice?: MedicalDeviceDTO;
};

export class OmnipodDeviceTransformer implements OmnipodTransformer<Devices> {
  _getSegment = (segment: OmnipodProfile["segments"][0]): InsulinPumpSegmentDTO => {
    return {
      start_time: DateUtils.formatPumpMillis(segment.start),
      end_time: DateUtils.formatPumpMillis(segment.end),
      basal_rate: segment.basalInsulin,
      insulin_to_carb_ratio: segment.carbsRatio,
      sensitivity: segment.insulinSensitivity,
      target_blood_glucose: segment.targetBG,
      blood_glucose_threshold: segment.thresholdBG,
    };
  };

  _getProfile =
    (data: OmnipodData) =>
    (profile: OmnipodProfile): InsulinPumpProfileDTO => {
      const segments = profile.segments.map(this._getSegment);

      return {
        id: profile.index.toString(),
        current_profile: profile.isActive,
        name: profile.name,
        active_insulin_time: data.info.pumpSettings.activeInsulinTime,
        basal_insulin_per_day: getBasalInsulinPerDay(segments),
        segments,
      };
    };

  transform = (data: OmnipodData): Devices => {
    return {
      pumpDevice: {
        display_name:
          data.info.model === "Eros" ? OmnipodConstants.EROS_DISPLAY_NAME : OmnipodConstants.DASH_DISPLAY_NAME,
        device_type_name:
          data.info.model === "Eros" ? OmnipodConstants.EROS_API_DEVICE_NAME : OmnipodConstants.DASH_API_DEVICE_NAME,
        serial_number: data.info.serialNumber,
        last_upload: DateTime.now().toISO(),
        last_upload_info: {
          last_fetched_date: data.endDate?.toISO() ?? "",
        } satisfies OmnipodLastUploadInfo,
        pump_data: {
          firmware_version: omnipodVersionToString(data.info.versions.pdm),
          profiles: data.info.profiles.filter(profile => !!profile.name).map(this._getProfile(data)),
          closed_loop_support: "unsupported_by_steto",
        },
        closed_loop_support: "unsupported_by_steto",
      },
      cgmDevice: undefined,
    };
  };
}
